import { Injectable } from '@angular/core';
import { forkJoin, Observable, take, throwError } from 'rxjs';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import { InitialDataResolver } from '../../app.resolvers';
import { HolidayService } from './holiday.service';
import { ClientService } from './client.service';
import { BoardService } from './board.service';
import { TreatmentService } from './treatment.service';
import { ServiceService } from './service.service';

@Injectable()
export class AuthService {
  private _authenticated: boolean = false;

  /**
   * Constructor
   */
  constructor(
    private _auth: AngularFireAuth,
    private initialDataResolver$: InitialDataResolver,
    private holiday$: HolidayService,
    private client$: ClientService,
    private board$: BoardService,
    private treatments$: TreatmentService,
    private service$: ServiceService
  ) {
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  load(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._auth.idTokenResult.subscribe(token => {
        if (token) {
          this.accessToken = token.token;

          forkJoin([
            this.client$.getAll().pipe(take(1)),
            this.board$.getAll().pipe(take(1)),
            this.treatments$.getAll().pipe(take(1)),
            this.service$.getAll().pipe(take(1)),
            this.holiday$.getAll().pipe(take(1))
          ]).subscribe((result => {
            resolve();
          }));

        } else {
          resolve();
        }
      });
    });

  }

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return new Observable((subscriber) => {
      this._auth.sendPasswordResetEmail(email)
        .then((result) => {
          subscriber.next();
          subscriber.complete();
        })
        .catch(() => {
          subscriber.error();
        });
    });
  }

  /**
   * Reset password
   *
   * @param password
   * @param code
   */
  resetPassword(password: string, code: string): Observable<any> {
    return new Observable((subscriber) => {
      this._auth.verifyPasswordResetCode(code)
        .then((result) => {
          this._auth.confirmPasswordReset(code, password)
            .then(() => {
              subscriber.next();
              subscriber.complete();
            })
            .catch(() => {
              subscriber.error();
            });
        })
        .catch(() => {
          subscriber.error();
        });
    });

  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signInWithCredentials(credentials: { email: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      // return throwError('User is already logged in.');
    }

    return new Observable((subscriber) => {
      this._auth.signInWithEmailAndPassword(credentials.email, credentials.password).then(() => {
        // Store the access token in the local storage
        this._auth.idToken.subscribe((accessToken) => {
          this.accessToken = accessToken;
        });

        // Set the authenticated flag to true
        this._authenticated = true;

        subscriber.next();
        subscriber.complete();
      }).catch((error) => {
        subscriber.error(error);
      });
    });
  }

  /**
   * Sign in with Google
   *
   */
  signInWithGoogle(): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return new Observable((subscriber) => {
      this._auth.signInWithPopup(new GoogleAuthProvider()).then(() => {
        // Store the access token in the local storage
        this._auth.idToken.subscribe((accessToken) => {
          this.accessToken = accessToken;
        });

        // Set the authenticated flag to true
        this._authenticated = true;

        this.initialDataResolver$.resolve().subscribe(() => {
          subscriber.next();
          subscriber.complete();
        });
      }).catch((error) => {
        subscriber.error(error);
      });
    });
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    await this._auth.signOut();
  }
}
